import React, { useState, useEffect, useCallback } from 'react';
import { Activity, Play, Pause, Clock, AlertCircle } from 'lucide-react';
import apiService from '../services/api';
import { toast } from 'react-hot-toast';
import { websocketService, SyncMessage } from '../services/websocket';
import { useData } from '../contexts/DataContext';

interface RealtimeData {
  data?: {
    powerflow?: any;
    storage?: any;
    inverter?: any;
  };
  timestamp?: string;
}

interface RealtimeStatus {
  user_id: number;
  is_running: boolean;
  next_run: string | null;
  cache_available: boolean;
  enabled_at: string | null;
  auto_disable_at: string | null;
}

const isNewerTimestamp = (incoming?: string, current?: string): boolean => {
  if (!incoming) return false;
  if (!current) return true;
  const incomingMs = Date.parse(incoming);
  const currentMs = Date.parse(current);
  if (Number.isNaN(incomingMs)) return false;
  if (Number.isNaN(currentMs)) return true;
  return incomingMs >= currentMs;
};

const LivePage: React.FC = () => {
  const { websocketConnected } = useData();
  const [isEnabled, setIsEnabled] = useState(false);
  const [loading, setLoading] = useState(false);
  const [realtimeData, setRealtimeData] = useState<RealtimeData | null>(null);
  const [status, setStatus] = useState<RealtimeStatus | null>(null);
  const [error, setError] = useState<string | null>(null);

  const applyRealtimeData = useCallback((incoming: RealtimeData | null | undefined) => {
    if (!incoming?.data) {
      return;
    }
    setRealtimeData((prev) => {
      if (prev && !isNewerTimestamp(incoming.timestamp, prev.timestamp)) {
        return prev;
      }
      return incoming;
    });
  }, []);

  const fetchStatus = useCallback(async () => {
    try {
      const data = await apiService.getRealtimeStatus();
      setStatus(data);
      setIsEnabled(data.is_running);
    } catch (err: any) {
      console.error('Error fetching status:', err);
    }
  }, []);

  const fetchRealtimeData = useCallback(async (showLoading: boolean = false) => {
    try {
      if (showLoading) {
        setLoading(true);
      }
      const data = await apiService.getRealtimeData();
      applyRealtimeData(data);
      setError(null);
      await fetchStatus();
    } catch (err: any) {
      console.error('Error fetching realtime data:', err);
      const errorMsg = err?.response?.data?.detail || 'Failed to fetch realtime data';
      setError(errorMsg);
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  }, [applyRealtimeData, fetchStatus]);

  useEffect(() => {
    console.log('🌐 [LivePage] Initializing - fetching data');
    fetchRealtimeData(true);
  }, [fetchRealtimeData]);

  // When the socket is down, poll REST so the page still catches up.
  // When it is live, WebSocket pushes are the source of truth.
  useEffect(() => {
    if (websocketConnected) {
      fetchRealtimeData(false);
      return;
    }

    const dataInterval = setInterval(() => fetchRealtimeData(false), 3000);
    return () => clearInterval(dataInterval);
  }, [websocketConnected, fetchRealtimeData]);

  useEffect(() => {
    const statusInterval = setInterval(fetchStatus, 5000);
    return () => clearInterval(statusInterval);
  }, [fetchStatus]);

  // WebSocket listener for realtime data updates
  useEffect(() => {
    const removeHandler = websocketService.onMessage((message: SyncMessage) => {
      if (message.type === 'realtime_data_update') {
        console.log('🌐 [LivePage] WebSocket update received');
        applyRealtimeData({
          data: message.data,
          timestamp: message.timestamp
        });
        setError(null);
      }
    });

    return removeHandler;
  }, [applyRealtimeData]);

  const handleEnable = async () => {
    setLoading(true);
    try {
      await apiService.enableRealtimeData();
      //toast.success(response.message || 'Realtime data enabled');
      setIsEnabled(true);
      await fetchStatus();
      // Fetch initial data immediately
      await fetchRealtimeData(false);
    } catch (err: any) {
      console.error('Error enabling realtime data:', err);
      const errorMsg = err?.response?.data?.detail || 'Failed to enable realtime data';
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleDisable = async () => {
    setLoading(true);
    try {
      await apiService.disableRealtimeData();
      //toast.success(response.message || 'Realtime data disabled');
      setIsEnabled(false);
      setRealtimeData(null);
      await fetchStatus();
    } catch (err: any) {
      console.error('Error disabling realtime data:', err);
      const errorMsg = err?.response?.data?.detail || 'Failed to disable realtime data';
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const formatTimestamp = (timestamp: string | null | undefined) => {
    if (!timestamp) return 'N/A';
    try {
      return new Date(timestamp).toLocaleString();
    } catch {
      return timestamp;
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
          <Activity className="w-8 h-8" />
          Live Data
        </h1>
        <p className="mt-2 text-gray-600">
          Real-time inverter data from your Fronius system
        </p>
      </div>

      {/* Control Panel */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-gray-900">Control</h2>
          <div className="flex items-center gap-2">
            <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
              isEnabled ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
            }`}>
              {isEnabled ? 'Active' : 'Inactive'}
            </span>
          </div>
        </div>

        <div className="flex gap-4 mb-4">
          <button
            onClick={handleEnable}
            disabled={loading || isEnabled}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium ${
              isEnabled
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'bg-green-600 text-white hover:bg-green-700'
            }`}
          >
            <Play className="w-4 h-4" />
            Enable Live Data
          </button>

          <button
            onClick={handleDisable}
            disabled={loading || !isEnabled}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium ${
              !isEnabled
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'bg-red-600 text-white hover:bg-red-700'
            }`}
          >
            <Pause className="w-4 h-4" />
            Disable Live Data
          </button>
        </div>

        {status && (
          <div className="space-y-2 text-sm text-gray-600 border-t pt-4">
            <div className="flex justify-between">
              <span>Enabled At:</span>
              <span className="font-mono">{formatTimestamp(status.enabled_at)}</span>
            </div>
            <div className="flex justify-between">
              <span>Next Update:</span>
              <span className="font-mono">{formatTimestamp(status.next_run)}</span>
            </div>
            <div className="flex justify-between">
              <span>Auto-Disable At:</span>
              <span className="font-mono">{formatTimestamp(status.auto_disable_at)}</span>
            </div>
            <div className="flex justify-between">
              <span>Cache Available:</span>
              <span className="font-mono">{status.cache_available ? 'Yes' : 'No'}</span>
            </div>
          </div>
        )}
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <div className="flex items-center gap-2 text-red-800">
            <AlertCircle className="w-5 h-5" />
            <span className="font-medium">Error: {error}</span>
          </div>
        </div>
      )}

      {/* Realtime Data Display */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-gray-900">Real-time Data</h2>
          {realtimeData?.timestamp && (
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <Clock className="w-4 h-4" />
              <span>Updated: {formatTimestamp(realtimeData.timestamp)}</span>
            </div>
          )}
        </div>

        {loading && !realtimeData ? (
          <div className="text-center py-8 text-gray-500">
            <Activity className="w-12 h-12 mx-auto mb-2 animate-pulse" />
            <p>Loading live data...</p>
          </div>
        ) : realtimeData ? (
          <div className="bg-gray-50 rounded-lg p-4 overflow-auto">
            <pre className="text-sm font-mono whitespace-pre-wrap">
              {JSON.stringify(realtimeData, null, 2)}
            </pre>
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            <Activity className="w-12 h-12 mx-auto mb-2 animate-pulse" />
            <p>Waiting for data...</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default LivePage;

